import React, { useMemo } from 'react';
import Icon from 'components/AppIcon';

const ProgressIndicator = ({
  currentQuestion = 1,
  totalQuestions = 10,
  completedQuestions = [],
  onQuestionSelect = null,
  showSteps = true,
  className = '',
}) => {
  const percentage = useMemo(
    () => Math.round((completedQuestions?.length / totalQuestions) * 100),
    [completedQuestions?.length, totalQuestions]
  );

  const getStepStatus = (questionIndex) => {
    const questionNumber = questionIndex + 1;
    if (completedQuestions?.includes(questionNumber)) return 'completed';
    if (questionNumber === currentQuestion) return 'current';
    return 'unanswered';
  };

  const handleStepClick = (questionNumber) => {
    if (onQuestionSelect) {
      onQuestionSelect(questionNumber);
    }
  };

  const handleStepKeyDown = (e, questionNumber) => {
    if (e?.key === 'Enter' || e?.key === ' ') {
      e?.preventDefault();
      handleStepClick(questionNumber);
    }
    if (e?.key === 'ArrowRight') {
      e?.preventDefault();
      const next = Math.min(questionNumber + 1, totalQuestions);
      document.getElementById(`step-${next}`)?.focus();
    }
    if (e?.key === 'ArrowLeft') {
      e?.preventDefault();
      const prev = Math.max(questionNumber - 1, 1);
      document.getElementById(`step-${prev}`)?.focus();
    }
  };

  // Show condensed steps on mobile (max 7 visible)
  const maxVisibleSteps = 7;
  const shouldCondense = totalQuestions > maxVisibleSteps;

  const getVisibleSteps = () => {
    if (!shouldCondense) return Array.from({ length: totalQuestions }, (_, i) => i + 1);
    const half = Math.floor(maxVisibleSteps / 2);
    let start = Math.max(1, currentQuestion - half);
    let end = Math.min(totalQuestions, start + maxVisibleSteps - 1);
    if (end - start < maxVisibleSteps - 1) {
      start = Math.max(1, end - maxVisibleSteps + 1);
    }
    return Array.from({ length: end - start + 1 }, (_, i) => start + i);
  };

  const visibleSteps = getVisibleSteps();

  return (
    <div
      className={`w-full ${className}`}
      role="region"
      aria-label="Progression du quiz"
    >
      {/* Summary Row */}
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Icon
            name="HelpCircle"
            size={16}
            color="var(--color-primary)"
            strokeWidth={2}
          />
          <span
            className="font-caption font-medium text-sm"
            style={{ color: 'var(--color-text-primary)' }}
          >
            Question{' '}
            <span
              className="font-mono font-semibold"
              style={{ color: 'var(--color-primary)' }}
            >
              {currentQuestion}
            </span>
            {' '}sur{' '}
            <span className="font-mono font-semibold">{totalQuestions}</span>
          </span>
        </div>

        <div className="flex items-center gap-2">
          {completedQuestions?.length > 0 && (
            <div className="flex items-center gap-1">
              <Icon
                name="CheckCircle"
                size={14}
                color="var(--color-success)"
                strokeWidth={2}
              />
              <span
                className="font-mono text-xs font-medium"
                style={{ color: 'var(--color-success)' }}
              >
                {completedQuestions?.length}/{totalQuestions}
              </span>
            </div>
          )}
          <span
            className="font-mono text-xs font-semibold px-2 py-0.5 rounded"
            style={{
              backgroundColor: 'var(--color-muted)',
              color: 'var(--color-primary)',
            }}
          >
            {percentage}%
          </span>
        </div>
      </div>
      {/* Progress Bar */}
      <div
        className="progress-bar-track mb-3"
        role="progressbar"
        aria-valuenow={percentage}
        aria-valuemin={0}
        aria-valuemax={100}
        aria-label={`${percentage}% complété`}
      >
        <div
          className="progress-bar-fill"
          style={{ width: `${percentage}%` }}
        />
      </div>
      {/* Step Indicators */}
      {showSteps && totalQuestions <= 20 && (
        <div
          className="flex items-center gap-1.5 flex-wrap"
          role="group"
          aria-label="Navigation par question"
        >
          {/* Show ellipsis if condensed and not starting from 1 */}
          {shouldCondense && visibleSteps?.[0] > 1 && (
            <span
              className="font-mono text-xs px-1"
              style={{ color: 'var(--color-muted-foreground)' }}
            >
              …
            </span>
          )}

          {visibleSteps?.map((questionNumber) => {
            const status = getStepStatus(questionNumber - 1);
            return (
              <button
                key={questionNumber}
                id={`step-${questionNumber}`}
                className={`progress-step ${status}`}
                onClick={() => handleStepClick(questionNumber)}
                onKeyDown={(e) => handleStepKeyDown(e, questionNumber)}
                aria-label={`Question ${questionNumber}${status === 'completed' ? ' (répondue)' : status === 'current' ? ' (actuelle)' : ''}`}
                aria-current={status === 'current' ? 'step' : undefined}
                tabIndex={status === 'current' ? 0 : -1}
                disabled={!onQuestionSelect}
                style={{ cursor: onQuestionSelect ? 'pointer' : 'default' }}
              >
                {status === 'completed' ? (
                  <Icon name="Check" size={12} color="currentColor" strokeWidth={3} />
                ) : (
                  questionNumber
                )}
              </button>
            );
          })}

          {/* Show ellipsis if condensed and not ending at last */}
          {shouldCondense && visibleSteps?.[visibleSteps?.length - 1] < totalQuestions && (
            <span
              className="font-mono text-xs px-1"
              style={{ color: 'var(--color-muted-foreground)' }}
            >
              …
            </span>
          )}
        </div>
      )}
      {/* Compact label for large question sets */}
      {totalQuestions > 20 && (
        <div className="flex items-center gap-4 mt-1">
          <div className="flex items-center gap-1.5">
            <div
              className="w-3 h-3 rounded-full"
              style={{ backgroundColor: 'var(--color-success)' }}
            />
            <span
              className="font-caption text-xs"
              style={{ color: 'var(--color-muted-foreground)' }}
            >
              Répondues ({completedQuestions?.length})
            </span>
          </div>
          <div className="flex items-center gap-1.5">
            <div
              className="w-3 h-3 rounded-full"
              style={{ backgroundColor: 'var(--color-primary)' }}
            />
            <span
              className="font-caption text-xs"
              style={{ color: 'var(--color-muted-foreground)' }}
            >
              En cours
            </span>
          </div>
          <div className="flex items-center gap-1.5">
            <div
              className="w-3 h-3 rounded-full border-2"
              style={{ borderColor: 'var(--color-border)' }}
            />
            <span
              className="font-caption text-xs"
              style={{ color: 'var(--color-muted-foreground)' }}
            >
              Restantes ({totalQuestions - completedQuestions?.length - 1})
            </span>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProgressIndicator;